import { CircularProgress, Stack, Box, Divider } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import BoldSubtitle from "src/components/Displays/BoldSubtitle";
import TextSubtitle from "src/components/Displays/TextSubtitle";
import StartCalculatorModal from "src/components/Modal/StartCalculatorModal";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { ROUTES } from "src/consts/routes";
import { useToggle } from "src/hooks/useToggle";
import { CheckCircle, PlayCircleBlack, StromPreiseSMA } from "src/assets";
import CertificateSection from "src/components/Common/CertificateSection";
import Text from "src/components/Displays/Text";
import CalculatedSavingsSummary from "src/pages/Tarif/components/CalculatedSavingsSummary";
import { SESSION_KEYS, BONUS } from "src/consts/session";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { Tariff } from "src/models/clientsModel";
import { useMemo, useState, useEffect, useRef } from "react";

const Content = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, toggleModal] = useToggle(false);

  const [searchParams] = useSearchParams();
  const tid = searchParams.get("tid") ?? "";
  const spid = searchParams.get("spid") ?? "";

  const isStageOrProdEnv =
    process.env.REACT_APP_BUILD_ENV === "STAGE" ||
    process.env.REACT_APP_BUILD_ENV === "PROD";

  const showOverlay =
    (tid.length === 0 || spid.length === 0) && isStageOrProdEnv;

  const handleStartCalculator = () => {
    if (showOverlay) {
      toggleModal();
      return;
    }
    navigate(ROUTES.PV_SETUP);
  };

  const handleModalSubmit = () => {
    const paPoSignUpUrl = process.env.REACT_APP_PAPO_SIGN_UP_URL;
    if (paPoSignUpUrl) window.open(paPoSignUpUrl, "_blank");
  };

  const [getSessionStorageItem] = useSessionStorage<Tariff[]>(
    SESSION_KEYS.TARIFFS,
  );

  const { tariffsData } = useMemo(() => {
    const tariffsData = getSessionStorageItem();
    return { tariffsData };
  }, [getSessionStorageItem]);

  const [showSpinner, setShowSpinner] = useState(true);

  const hasTariffs = Array.isArray(tariffsData) && tariffsData.length > 0;

  const [, setTariffs] = useSessionStorage<Tariff[]>(SESSION_KEYS.TARIFFS);
  const [, setBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);

  const openSummary = searchParams.get("openSummary") === "true";

  const videoRef = useRef<HTMLVideoElement>(null);

  const [, setShowVideo] = useState({
    width: "0px",
    height: "0px",
  });

  document.addEventListener("fullscreenchange", (event) => {
    if (document.fullscreenElement === null) {
      videoRef.current?.pause();
      setShowVideo({
        width: "0px",
        height: "0px",
      });
    }
  });

  if (videoRef.current) {
    videoRef.current.onpause = function () {
      if (videoRef.current?.seeking) {
        return;
      }
      videoRef.current?.pause();
      setShowVideo({
        width: "0px",
        height: "0px",
      });
    };
  }

  const handleVideoClick = () => {
    setShowVideo({
      width: "100%",
      height: "100%",
    });
    const video: any = document.getElementsByTagName("video")[0];

    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    } else if (video.mozRequestFullScreen) {
      video.mozRequestFullScreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    }

    video.play();
  };

  useEffect(() => {
    if (!openSummary && tid === "") {
      setShowSpinner(false);
      return;
    }

    let latestTariffs: Tariff[] = [];

    try {
      const initialData = sessionStorage.getItem(SESSION_KEYS.TARIFFS);
      if (initialData) {
        latestTariffs = JSON.parse(initialData);
        setTariffs(latestTariffs);
      }
    } catch {}

    if (openSummary) {
      setShowSpinner(false);
      return;
    }

    const interval = setInterval(() => {
      try {
        const storage = sessionStorage.getItem(SESSION_KEYS.TARIFFS);
        if (!storage) return;

        const parsed = JSON.parse(storage) as Tariff[];

        if (JSON.stringify(parsed) !== JSON.stringify(latestTariffs)) {
          latestTariffs = parsed;
          setTariffs(parsed);
        }
      } catch {}
    }, 500);

    const timer = setTimeout(() => {
      if (latestTariffs.length > 0) {
        setShowSpinner(false);
      } else {
        setShowSpinner(false);
      }

      clearInterval(interval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [openSummary, tid, navigate, setTariffs]);

  return (
    <Stack
      gap={6}
      sx={{
        width: "100%",
        maxWidth: "880px",
      }}
    >
      {showSpinner && (
        <Box
          width="100%"
          minHeight="200px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress sx={{ color: "gray" }} />
        </Box>
      )}

      {!showSpinner && (
        <Stack
          gap={2}
          width="100%"
          alignItems={{ xs: "center", sm: "flex-start" }}
        >
          {hasTariffs && (
            <>
              <Text
                sx={{
                  color: "black",
                  fontSize: "20px",
                  fontWeight: "600",
                  lineHeight: "26px",
                  fontFamily: "isonType",
                  letterSpacing: "-0.4px",
                  mb: 4,
                }}
                text={`${t("page.calculatedSavingsSummary.title")}:`}
              />
              <Box width={{ xs: "100%", sm: "auto" }}>
                <CalculatedSavingsSummary
                  tariffsData={tariffsData}
                  showSavingsResult={true}
                  openByDefault={openSummary}
                  getLogo={() => ({ houseImg: "/path/to/img", cssProps: {} })}
                />
              </Box>
              <PrimaryButton
                onClick={() => navigate(ROUTES.TARIF)}
                sx={{
                  textTransform: "none",
                  width: { xs: "100%", sm: "28.4%" },
                  mt: 4,
                  minHeight: "50px",
                  minWidth: "250px",
                  fontSize: "18px",
                  alignSelf: "center",
                }}
                text={t("page.calculatedSavingsSummary.tarifDetails")}
              />
            </>
          )}

          <PrimaryButton
            onClick={() => {
              setBonus(null);
              setTariffs([]);
              handleStartCalculator();
            }}
            sx={(theme) => ({
              width: hasTariffs
                ? { xs: "100%", sm: "20%" }
                : { xs: "100%", sm: "250px" },
              alignSelf: hasTariffs
                ? "center"
                : { xs: "center", sm: "flex-start" },
              textTransform: "none",
              fontSize: hasTariffs ? "12px" : "18px",
              padding: hasTariffs ? "4px 12px" : undefined,
              maxHeight: hasTariffs ? "32px" : "50px",
              minHeight: hasTariffs ? "32px" : "50px",
              minWidth: hasTariffs ? "auto" : "250px",
              mt: 2,
              border: hasTariffs ? "1px solid #000000" : "none",
              bgcolor: hasTariffs
                ? theme.palette.secondaryButton.background
                : undefined,
              color: hasTariffs
                ? theme.palette.secondaryButton.text
                : undefined,
              "&:hover": {
                bgcolor: hasTariffs
                  ? theme.palette.secondaryButton.background
                  : undefined,
              },
            })}
            text={
              hasTariffs
                ? t("page.introduction.textNewCalculation")
                : t("page.introduction.textCalculation")
            }
          />
        </Stack>
      )}

      <Divider />

      <BoldSubtitle text={t("page.introductionSMA.textTitle")} />
      <Stack gap={6} mt={-4}>
        <TextSubtitle
          sx={{
            fontWeight: "700",
            fontSize: { xs: "16px", sm: "20px" },
            lineHeight: { xs: "22px", sm: "30px" },
          }}
          text={t("page.introductionSMA.textSubtitleOne")}
        />

        <Stack pl={5} gap={2}>
          <Stack direction={"row"} gap={2}>
            <Stack pt={{ xs: 0.5, sm: 1 }}>
              <CheckCircle />
            </Stack>
            <Stack>
              <TextSubtitle
                sx={{
                  fontSize: { xs: "16px", sm: "20px" },
                  fontWeight: { xs: 500, sm: 400 },
                  lineHeight: { xs: "22px", sm: "30px" },
                }}
                text={
                  <Trans
                    i18nKey={
                      t(
                        "page.introductionSMA.textSubtitleOnePoint1",
                      ) as "page.introductionSMA.textSubtitleOnePoint1"
                    }
                    t={t}
                    components={{ bold: <strong />, br: <br /> }}
                  />
                }
              />
            </Stack>
          </Stack>

          <Stack direction={"row"} gap={2}>
            <Stack pt={{ xs: 0.5, sm: 1 }}>
              <CheckCircle />
            </Stack>
            <Stack>
              <TextSubtitle
                sx={{
                  fontSize: { xs: "16px", sm: "20px" },
                  fontWeight: { xs: 500, sm: 400 },
                  lineHeight: { xs: "22px", sm: "30px" },
                }}
                text={
                  <Trans
                    i18nKey={
                      t(
                        "page.introductionSMA.textSubtitleOnePoint2",
                      ) as "page.introductionSMA.textSubtitleOnePoint2"
                    }
                    t={t}
                    components={{ bold: <strong />, br: <br /> }}
                  />
                }
              />
            </Stack>
          </Stack>

          <Stack direction={"row"} gap={2}>
            <Stack pt={{ xs: 0.5, sm: 1 }}>
              <CheckCircle />
            </Stack>
            <Stack>
              <TextSubtitle
                sx={{
                  fontSize: { xs: "16px", sm: "20px" },
                  fontWeight: { xs: 500, sm: 400 },
                  lineHeight: { xs: "22px", sm: "30px" },
                }}
                text={
                  <Trans
                    i18nKey={
                      t(
                        "page.introductionSMA.textSubtitleOnePoint3",
                      ) as "page.introductionSMA.textSubtitleOnePoint3"
                    }
                    t={t}
                    components={{ bold: <strong />, br: <br /> }}
                  />
                }
              />
            </Stack>
          </Stack>

          <Stack direction={"row"} gap={2}>
            <Stack pt={{ xs: 0.5, sm: 1 }}>
              <CheckCircle />
            </Stack>
            <Stack>
              <TextSubtitle
                sx={{
                  fontSize: { xs: "16px", sm: "20px" },
                  fontWeight: { xs: 500, sm: 400 },
                  lineHeight: { xs: "22px", sm: "30px" },
                }}
                text={
                  <Trans
                    i18nKey={
                      t(
                        "page.introductionSMA.textSubtitleOnePoint4",
                      ) as "page.introductionSMA.textSubtitleOnePoint4"
                    }
                    t={t}
                    components={{ bold: <strong />, br: <br /> }}
                  />
                }
              />
            </Stack>
          </Stack>

          <Stack direction={"row"} gap={2}>
            <Stack pt={{ xs: 0.5, sm: 1 }}>
              <CheckCircle />
            </Stack>
            <Stack>
              <TextSubtitle
                sx={{
                  fontSize: { xs: "16px", sm: "20px" },
                  fontWeight: { xs: 500, sm: 400 },
                  lineHeight: { xs: "22px", sm: "30px" },
                }}
                text={
                  <Trans
                    i18nKey={
                      t(
                        "page.introductionSMA.textSubtitleOnePoint5",
                      ) as "page.introductionSMA.textSubtitleOnePoint5"
                    }
                    t={t}
                    components={{ bold: <strong />, br: <br /> }}
                  />
                }
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack gap={6}>
        <BoldSubtitle text={t("page.introductionSMA.titleEnergyBuy")} />
        <Stack pl={5} gap={2} sx={{ width: "100%" }}>
          <PrimaryButton
            onClick={handleVideoClick}
            text={
              <>
                <Stack direction="row" alignItems={"center"} gap={2}>
                  <PlayCircleBlack />

                  {t("page.introductionSMA.titleVideo")}
                </Stack>
              </>
            }
            sx={{
              color: "#000000ff",
              width: { xs: "100%", sm: "fit-content" },
              fontSize: "16px",
              bgcolor: "rgba(255, 255, 255, 0)",
              border: "2px solid #000000ff",
              "&:hover": {
                backgroundColor: "#00000000",
              },
            }}
          />
        </Stack>
        <StromPreiseSMA
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      </Stack>

      <Stack alignItems={{ xs: "center", sm: "flex-start" }}>
        <CertificateSection />
      </Stack>

      <Text
        text={t("page.introductionSMA.footNote")}
        sx={{
          lineHeight: "21px",
        }}
      />
      <Text
        text={t("page.introductionSMA.footNote2")}
        sx={{
          lineHeight: "21px",
        }}
      />
      <Divider />
      <StartCalculatorModal
        isOpen={isModalOpen}
        onClose={toggleModal}
        onSubmit={handleModalSubmit}
        title={t("page.introductionSMA.modal.title")}
        subtitle1={t("page.introductionSMA.modal.subtitle1")}
        subtitle2={t("page.introductionSMA.modal.subtitle2")}
        subtitle3={t("page.introductionSMA.modal.subtitle3")}
        submitButtonText={t("page.introductionSMA.modal.submitButtonText")}
      />
    </Stack>
  );
};

export default Content;
